import React from 'react';
import { Button } from '@unmind/design-system';
import { UserMessage, Error } from 'Assistant/Chatbot';
import { useConversationStarters } from 'Assistant/Chatbot/hooks';
import { v4 as uuidv4 } from 'uuid';
import LoadingIndicator from 'Shared/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import { tracking } from 'App/Tracking';
import { MappedMessageType } from 'Assistant/__generated__/client.schemas';

type ChatbotStartersProps = {
  handleSubmitRedirect(message: UserMessage): Promise<void>;
};

const ChatbotStarters = ({ handleSubmitRedirect }: ChatbotStartersProps) => {
  const {
    conversationStarters,
    isLoadingConversationStarters,
    errorFetchingConversationStarters,
    refetchConversationStarters,
  } = useConversationStarters();
  const { t: translate } = useTranslation('chatbot');

  if (errorFetchingConversationStarters) {
    return (
      <Error
        title={translate('chatbot_starters.fetch_conversation_starters_error')}
        orientation="vertical"
        onTryAgainPress={async () => {
          await refetchConversationStarters();
        }}
      />
    );
  }

  return (
    <div className="conversation-starters">
      {isLoadingConversationStarters ? (
        <LoadingIndicator />
      ) : (
        conversationStarters.map(starter => (
          <Button
            className="conversation-starter"
            variant="secondary"
            key={starter.id}
            aria-label={`Start a conversation: ${starter.text}`}
            onClick={async () => {
              tracking.track('ai-coach-chatbot-starter-suggestion-clicked', {
                source: 'homepage-chatbot-ui',
                message: `${starter.emoji} ${starter.text}`,
                topic: starter.topic,
                starterId: starter.id,
              });

              void handleSubmitRedirect({
                id: uuidv4(),
                createdAt: new Date().toISOString(),
                content: starter.text,
                topic: starter.topic,
                starterId: starter.id,
                type: MappedMessageType.text,
              });
            }}
          >
            <span aria-hidden>{starter.emoji}</span>
            <span className="label-s">{starter.text}</span>
          </Button>
        ))
      )}
    </div>
  );
};

export { ChatbotStarters };
